<template>
    <div v-if="visible" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h3>📄 جزئیات بازرگانی</h3>
                <div class="modal-actions">
                    <button 
                        v-if="details.length > 0 && !loading && !error"
                        class="print-modal-btn"
                        @click="printDetails"
                        title="چاپ جزئیات"
                    >
                        🖨️ چاپ
                    </button>
                    <button class="close-btn" @click="closeModal">×</button>
                </div>
            </div>
            
            <div class="modal-body" id="commercial-details-print">
                <!-- Loading State -->
                <div v-if="loading" class="modal-loading">
                    <div class="loading-spinner"></div>
                    <span>در حال دریافت اطلاعات جزئیات...</span>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="modal-error">
                    <div class="error-icon">⚠️</div>
                    <h4>خطا در دریافت اطلاعات</h4>
                    <p>{{ error }}</p>
                    <button class="retry-btn" @click="retryFetch">
                        تلاش مجدد
                    </button>
                </div>

                <!-- Success State -->
                <div v-else-if="details.length > 0" class="commercial-details">
                    <div class="details-grid">
                        <div 
                            v-for="(detail, index) in details" 
                            :key="index"
                            class="detail-card"
                        >
                            <!-- Title Section -->
                            <div class="detail-section">
                                <h4 class="section-title">عنوان کالا</h4>
                                <div class="info-box">
                                    <div class="info-row">
                                        <span class="info-label">نام کالا:</span>
                                        <span class="info-value">{{ detail.KalaName || 'نامشخص' }}</span>
                                    </div>

                                    <!-- Amari Section -->
                                    <div class="info-row" v-if="getItemsWithLabels(detail, '13').length > 0">
                                        <span class="info-label">آماری:</span>
                                        <div class="multi-values">
                                            <span class="multi-value-item" v-for="item in getItemsWithLabels(detail, '13')" :key="item.key">
                                                {{ item.label }}: {{ formatNumber(item.value) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="info-row">
                                        <span class="info-label">واحد:</span>
                                        <span class="info-value">{{ detail.vahed || 'نامشخص' }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Values Section -->
                            <div class="detail-section">
                                <h4 class="section-title">مقادیر</h4>
                                <div class="info-box">
                                    <div class="info-row" v-if="detail.GM7">
                                        <span class="info-label">وزن پر:</span>
                                        <span class="info-value">{{ formatNumber(detail.GM7) }}</span>
                                    </div>
                                    <div class="info-row" v-if="detail.GM8">
                                        <span class="info-label">وزن خالی:</span>
                                        <span class="info-value">{{ formatNumber(detail.GM8) }}</span>
                                    </div>
                                    <div class="info-row" v-if="detail.GM6">
                                        <span class="info-label">مقدار ناخالص:</span>
                                        <span class="info-value">{{ formatNumber(detail.GM6) }}</span>
                                    </div>
                                    <!-- Ksorat Section -->
                                    <div class="info-row" v-if="getItemsWithLabels(detail, '11').length > 0">
                                        <span class="info-label">کسورات:</span>
                                        <div class="multi-values">
                                            <span class="multi-value-item" v-for="item in getItemsWithLabels(detail, '11')" :key="item.key">
                                                {{ item.label }}: {{ formatNumber(item.value) }}
                                            </span>
                                        </div>
                                    </div>
                                    <!-- Ezafat Section -->
                                    <div class="info-row" v-if="getItemsWithLabels(detail, '12').length > 0">
                                        <span class="info-label">اضافات:</span>
                                        <div class="multi-values">
                                            <span class="multi-value-item" v-for="item in getItemsWithLabels(detail, '12')" :key="item.key">
                                                {{ item.label }}: {{ formatNumber(item.value) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="info-row" v-if="detail.value1">
                                        <span class="info-label">مقدار خالص:</span>
                                        <span class="info-value">{{ formatNumber(detail.value1) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Amounts Section -->
                            <div class="detail-section">
                                <h4 class="section-title">مبالغ</h4>
                                <div class="info-box">
                                    <div class="info-row">
                                        <span class="info-label">فی:</span>
                                        <span class="info-value amount">{{ formatCurrency(detail.Fe) }}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">مبلغ کل:</span>
                                        <span class="info-value amount total">{{ formatCurrency(detail.money) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Additional Information -->
                            <div class="detail-section" v-if="hasAdditionalInfo(detail)">
                                <h4 class="section-title">اطلاعات تکمیلی</h4>
                                <div class="info-box">
                                    <div class="info-row" v-if="detail.Comment2 && detail.Comment2 !== '-'">
                                        <span class="info-label">شرح 2:</span>
                                        <span class="info-value">{{ detail.Comment2 }}</span>
                                    </div>
                                    <div class="info-row" v-if="detail.Comment && detail.Comment !== '-'">
                                        <span class="info-label">شرح:</span>
                                        <span class="info-value">{{ detail.Comment }}</span>
                                    </div>
                                    <div class="info-row" v-if="detail.Codeing">
                                        <span class="info-label">کدینگ:</span>
                                        <span class="info-value">{{ detail.Codeing }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="modal-empty">
                    <div class="empty-icon">📦</div>
                    <h4>هیچ اطلاعات جزئیاتی یافت نشد</h4>
                    <p>برای این فاکتور اطلاعات بازرگانی ثبت نشده است.</p>
                </div>
            </div>

            <div class="modal-footer">
                <button class="close-modal-btn" @click="closeModal">
                    بستن
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch } from 'vue'
import { employerService } from '@/services/api';

export default {
    name: 'CommercialDetailsModal',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        invoiceId: {
            type: [Number, String],
            default: null
        },
        ac: {
            type: Number,
            default: null
        },
        actCode: {
            type: Number,
            default: null
        },
        title: {
            type: String,
            default: 'جزئیات بازرگانی'
        }
    },
    emits: ['update:visible', 'close', 'print'],
    setup(props, { emit }) {
        const details = ref([])
        const loading = ref(false)
        const error = ref(null)

        const fetchDetails = async () => {
            if (!props.invoiceId || !props.ac || !props.actCode) {
                error.value = 'اطلاعات کافی برای دریافت جزئیات وجود ندارد'
                return
            }

            loading.value = true
            error.value = null
            details.value = []

            try {
                const response = await employerService.getFactorDetails(props.invoiceId, props.ac, props.actCode)
                if (response.data.success) {
                    details.value = response.data.data || []
                } else {
                    error.value = response.data.message || 'خطا در دریافت اطلاعات جزئیات'
                }
            } catch (err) {
                console.error('Error fetching commercial details:', err)
                if (err.response?.data?.message) {
                    error.value = err.response.data.message
                } else if (err.code === 'NETWORK_ERROR') {
                    error.value = 'خطا در اتصال به سرور'
                } else {
                    error.value = 'خطای ناشناخته در دریافت اطلاعات'
                }
            } finally {
                loading.value = false
            }
        }

        const closeModal = () => {
            emit('update:visible', false)
            emit('close')
        }

        const retryFetch = () => {
            fetchDetails()
        }

const printDetails = () => {
    emit('print')
    
    if (!details.value || details.value.length === 0) {
        alert('هیچ داده‌ای برای چاپ وجود ندارد')
        return
    }
    
    const printWindow = window.open('', '_blank', 'width=1200,height=800')
    
    if (!printWindow) {
        alert('لطفاً مسدود کننده پنجره‌های بازشو را غیرفعال کنید')
        return
    }
    
    const title = props.title || 'جزئیات بازرگانی'
    const invoiceId = props.invoiceId || '-'
    
    // Generate table rows
    const tableRows = details.value.map((detail, index) => {
        return `
            <tr>
                <td>${index + 1}</td>
                <td>${detail.KalaName || 'نامشخص'}</td>
                <td>${detail.vahed || 'نامشخص'}</td>
                <td>${formatNumber(detail.value1 || 0)}</td>
                <td>${formatCurrency(detail.Fe || 0)}</td>
                <td>${formatCurrency(detail.money || 0)}</td>
                <td>${detail.Comment || detail.Comment2 || '-'}</td>
            </tr>
        `
    }).join('')
    
    // Calculate totals
    const totalAmount = details.value.reduce((sum, d) => sum + (d.money || 0), 0)
    const totalQuantity = details.value.reduce((sum, d) => sum + (d.value1 || 0), 0)
    
    const printContent = `
        <!DOCTYPE html>
        <html dir="rtl">
            <head>
                <meta charset="UTF-8">
                <title>چاپ جزئیات بازرگانی</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: 'Tahoma', 'Vazir', Arial, sans-serif; 
                        margin: 20px; 
                        padding: 20px;
                        direction: rtl;
                        line-height: 1.6;
                        background: #ffffff;
                        color: #333;
                    }
                    .print-header { 
                        text-align: center; 
                        margin-bottom: 30px;
                        padding-bottom: 15px;
                        border-bottom: 3px solid #667eea;
                    }
                    .print-header h1 {
                        margin: 0 0 5px 0;
                        color: #2d3436;
                        font-size: 24px;
                    }
                    .print-header .subtitle {
                        color: #666;
                        font-size: 14px;
                    }
                    .print-info {
                        display: flex;
                        justify-content: space-between;
                        flex-wrap: wrap;
                        margin-bottom: 20px;
                        padding: 15px 20px;
                        background: #f8f9fa;
                        border-radius: 8px;
                        border: 1px solid #e0e0e0;
                        gap: 10px;
                    }
                    .info-item {
                        display: flex;
                        flex-direction: column;
                    }
                    .info-label {
                        font-weight: bold;
                        color: #555;
                        margin-bottom: 5px;
                        font-size: 13px;
                    }
                    .info-value {
                        color: #333;
                        font-size: 14px;
                    }
                    .table-container {
                        overflow-x: auto;
                        margin-top: 20px;
                        border-radius: 8px;
                        border: 1px solid #e0e0e0;
                    }
                    .report-table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 13px;
                    }
                    .report-table th {
                        background: #667eea;
                        color: white;
                        padding: 10px 12px;
                        text-align: center;
                        border: 1px solid #5a6fd6;
                        font-weight: bold;
                        font-size: 12px;
                        white-space: nowrap;
                    }
                    .report-table td {
                        padding: 8px 12px;
                        border: 1px solid #ddd;
                        text-align: center;
                        font-size: 12px;
                    }
                    .report-table tr:nth-child(even) {
                        background-color: #f8f9fa;
                    }
                    .report-table tr:hover {
                        background-color: #e8e8e8;
                    }
                    .total-row {
                        background-color: #e3f2fd !important;
                        font-weight: bold;
                    }
                    .total-row td {
                        border-top: 2px solid #667eea;
                        padding: 10px 12px;
                    }
                    .footer {
                        margin-top: 30px;
                        text-align: center;
                        color: #888;
                        font-size: 12px;
                        border-top: 1px solid #eee;
                        padding-top: 20px;
                    }
                    .footer p {
                        margin: 5px 0;
                    }
                    @media print {
                        body { margin: 0; padding: 15px; font-size: 11px; }
                        .print-header { margin-bottom: 15px; padding-bottom: 10px; }
                        .print-header h1 { font-size: 20px; }
                        .print-info { padding: 10px 15px; margin-bottom: 15px; }
                        .report-table { font-size: 10px; }
                        .report-table th { padding: 6px 8px; font-size: 10px; }
                        .report-table td { padding: 5px 8px; font-size: 10px; }
                    }
                    @page { margin: 0.5cm; size: A4 portrait; }
                </style>
            </head>
            <body>
                <div class="print-header">
                    <h1>📄 ${title}</h1>
                    <div class="subtitle">شماره فاکتور: ${invoiceId}</div>
                </div>
                
                <div class="print-info">
                    <div class="info-item">
                        <span class="info-label">📅 تاریخ چاپ:</span>
                        <span class="info-value">${new Date().toLocaleDateString('fa-IR')}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">📋 تعداد اقلام:</span>
                        <span class="info-value">${details.value.length} مورد</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">📦 مجموع تعداد:</span>
                        <span class="info-value">${formatNumber(totalQuantity)}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">💰 مجموع مبلغ:</span>
                        <span class="info-value">${formatCurrency(totalAmount)}</span>
                    </div>
                </div>
                
                <div class="table-container">
                    <table class="report-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>نام کالا</th>
                                <th>واحد</th>
                                <th>تعداد</th>
                                <th>فی</th>
                                <th>مبلغ کل</th>
                                <th>شرح</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                            <tr class="total-row">
                                <td colspan="3" style="text-align: left;">
                                    <strong>جمع کل:</strong>
                                </td>
                                <td><strong>${formatNumber(totalQuantity)}</strong></td>
                                <td></td>
                                <td><strong>${formatCurrency(totalAmount)}</strong></td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <div class="footer">
                    <p>الماس هوشمند - سیستم حسابداری پیشرفته</p>
                    <p>تاریخ تولید گزارش: ${new Date().toLocaleString('fa-IR')}</p>
                </div>
            </body>
        </html>
    `
    
    printWindow.document.write(printContent)
    printWindow.document.close()
    printWindow.focus()
    
    setTimeout(() => {
        printWindow.print()
    }, 1000)
}

        const formatNumber = (number) => {
            if (!number && number !== 0) return '۰'
            return new Intl.NumberFormat('fa-IR').format(number)
        }

        const formatCurrency = (amount) => {
            if (!amount && amount !== 0) return '۰'
            return new Intl.NumberFormat('fa-IR').format(amount)
        }

        const getItemsWithLabels = (detail, prefix) => {
            const items = []
            for (const [key, value] of Object.entries(detail)) {
                if (key.startsWith(prefix) && value && value !== 0) {
                    const label = detail._field_labels?.[key] || key
                    items.push({ key, value, label })
                }
            }
            return items
        }

        const hasAdditionalInfo = (detail) => {
            return (detail.Comment2 && detail.Comment2 !== '-') || 
                   (detail.Comment && detail.Comment !== '-') || 
                   detail.Codeing
        }

        // Watch for visibility changes to fetch data
        watch(() => props.visible, (newVal) => {
            if (newVal && props.invoiceId && props.ac && props.actCode) {
                fetchDetails()
            }
        }, { immediate: true })

        return {
            details,
            loading,
            error,
            fetchDetails,
            closeModal,
            retryFetch,
            printDetails,
            formatNumber,
            formatCurrency,
            getItemsWithLabels,
            hasAdditionalInfo
        }
    }
}
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--modal-overlay, rgba(0, 0, 0, 0.6));
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    padding: 20px;
    backdrop-filter: blur(4px);
}

.modal-content {
    background: var(--card-bg, #ffffff);
    border-radius: 16px;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border, #e0e0e0);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border, #e0e0e0);
    flex-shrink: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 16px 16px 0 0;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.modal-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.print-modal-btn {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
}

.print-modal-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
}

.close-btn {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: white;
    padding: 0 8px;
    transition: all 0.3s ease;
    opacity: 0.8;
}

.close-btn:hover {
    opacity: 1;
    transform: rotate(90deg);
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
    flex: 1;
}

.modal-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--border, #e0e0e0);
    display: flex;
    justify-content: flex-end;
    flex-shrink: 0;
}

/* Loading State */
.modal-loading {
    text-align: center;
    padding: 40px 20px;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid var(--border, #e0e0e0);
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

/* Error State */
.modal-error {
    text-align: center;
    padding: 40px 20px;
    color: #d63031;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 12px;
}

.modal-error h4 {
    margin: 0 0 10px 0;
    color: #d63031;
}

.modal-error p {
    margin: 0 0 20px 0;
    color: var(--text-muted, #666);
}

.retry-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: transform 0.3s ease;
}

.retry-btn:hover {
    transform: translateY(-2px);
}

/* Empty State */
.modal-empty {
    text-align: center;
    padding: 60px 20px;
}

.empty-icon {
    font-size: 60px;
    color: var(--text-muted, #999);
    margin-bottom: 16px;
}

.modal-empty h4 {
    margin: 0 0 10px 0;
    color: var(--heading, #333);
}

.modal-empty p {
    color: var(--text-muted, #666);
}

/* Commercial Details */
.commercial-details {
    margin-top: 10px;
}

.details-grid {
    display: grid;
    gap: 20px;
}

.detail-card {
    background: var(--bg, #f8f9fa);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid var(--border, #e0e0e0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.detail-section {
    margin-bottom: 20px;
}

.detail-section:last-child {
    margin-bottom: 0;
}

.section-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--heading, #333);
    margin: 0 0 12px 0;
}

.info-box {
    background: var(--card-bg, #ffffff);
    border-radius: 8px;
    padding: 16px;
    border: 1px solid var(--border, #e0e0e0);
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
    border-bottom: none;
}

.info-label {
    font-weight: 600;
    color: var(--text-muted, #555);
    font-size: 14px;
    flex-shrink: 0;
}

.info-value {
    color: var(--text, #333);
    font-size: 14px;
    font-weight: 500;
    text-align: left;
}

.info-value.amount {
    color: #2d3436;
    font-weight: 600;
}

.info-value.amount.total {
    color: #e74c3c;
    font-size: 16px;
}

.multi-values {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    align-items: flex-end;
}

.multi-value-item {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 2px 0;
    gap: 10px;
}

.close-modal-btn {
    padding: 10px 24px;
    background: var(--secondary, #6c757d);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.3s ease;
}

.close-modal-btn:hover {
    background: #5a6268;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
    .modal-content {
        max-width: 100%;
        max-height: 95vh;
        border-radius: 12px;
    }
    
    .modal-header {
        padding: 16px 20px;
        border-radius: 12px 12px 0 0;
    }
    
    .modal-body {
        padding: 16px;
    }
    
    .detail-card {
        padding: 16px;
    }
    
    .info-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }
    
    .info-label {
        width: 100%;
    }
    
    .info-value {
        width: 100%;
        text-align: right;
    }
    
    .multi-values {
        width: 100%;
        align-items: flex-start;
    }
    
    .modal-actions {
        gap: 8px;
    }
    
    .print-modal-btn {
        padding: 6px 12px;
        font-size: 12px;
    }
}
.bank-app {
    min-height: 100vh;
    background: var(--bg);
    padding: 20px;
    direction: rtl;
    margin-bottom: 45px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--card-bg);
    border-radius: 15px;
    padding: 20px 24px;
    margin-bottom: 20px;
    margin-top: 20px;
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--border);
}

.page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: var(--heading);
}

.header-actions {
    display: flex;
    gap: 10px;
}

.refresh-btn {
    padding: 8px 16px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text);
}

.refresh-btn:hover:not(:disabled) {
    background: var(--ring);
    border-color: var(--primary);
}

.refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.refresh-icon.spinning {
    animation: spin 1s linear infinite;
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 60px 20px;
}

.spinner {
    border: 4px solid var(--border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

/* Error State */
.error-state {
    text-align: center;
    padding: 40px 20px;
    background: #fff3cd;
    border-radius: 12px;
    border: 1px solid #ffc107;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 12px;
}

.error-message {
    color: #856404;
    margin-bottom: 16px;
}

.retry-btn {
    padding: 10px 24px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s ease;
}

.retry-btn:hover {
    background: var(--primary-hover);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px dashed var(--border);
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.empty-state h3 {
    margin: 0 0 8px;
    color: var(--heading);
}

.empty-state p {
    color: var(--text-muted);
}
.kala-actions {
    display: flex;
    gap: 10px;
}
.view-details-btn,
.add-to-cart-btn {
    padding: 8px 15px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    justify-content: center;
}

.view-details-btn {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--border);
}

.view-details-btn:hover {
    background: var(--hover);
    transform: translateY(-2px);
}
.btn-icon {
    font-size: 18px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

/* Loading State */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.modal-loading .loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.modal-error {
  text-align: center;
  padding: 40px;
  color: #d63031;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-error h4 {
  margin: 0 0 10px 0;
  color: #d63031;
}

.modal-error p {
  margin: 0 0 20px 0;
  color: #666;
}

.retry-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: transform 0.2s;
}

.retry-btn:hover {
  transform: translateY(-2px);
}

/* Empty State */
.modal-empty {
  text-align: center;
  padding: 40px;
  color: #666;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-empty h4 {
  margin: 0 0 10px 0;
  color: #333;
}

/* Commercial Details Styles */
.commercial-details {
  margin-top: 10px;
}

.details-grid {
  display: grid;
  gap: 20px;
}

.detail-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.info-box {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
}

        /* Multi-values styling */
        .multi-values {
            display: flex;
            flex-direction: column;
            gap: 5px;
            flex: 1;
        }
        
        .multi-value-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 0;
        }
        
        .multi-value-code {
            font-weight: 600;
            color: #4b6cb7;
            margin-left: 10px;
        }
        
        .multi-value-amount {
            color: #2c3e50;
            font-weight: 500;
        }

.info-value.amount {
  font-weight: 600;
  color: #2d3436;
}

.info-value.amount.total {
  color: #e74c3c;
  font-size: 1rem;
}

/* Details Button */
.details-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 10px;
  transition: all 0.3s ease;
  width: 100%;
}

.details-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.details-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.close-modal-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.close-modal-btn:hover {
  background: #5a6268;
}

/* Responsive Design */
@media (max-width: 768px) {
  
  .modal-header {
    padding: 15px;
  }
  
  .detail-card {
    padding: 15px;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .info-label, .info-value {
    font-size: 0.85rem;
  }
}
.modal-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.print-modal-btn {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.print-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(39, 174, 96, 0.3);
}

/* Update modal header to accommodate the print button */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Ensure the header content is properly aligned */
.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  flex: 1;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .modal-actions {
    gap: 8px;
  }
  
  .print-modal-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
  
  .modal-header {
    padding: 15px;
  }
}
.accounting-section {
  padding-bottom: 45px;
  padding-top: 15px;
  background-color: var(--bg);
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  /*padding-bottom: 12px;
   border-bottom: 1px solid #f0f0f0; */
}

.record-date {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: #e74c3c; /* Red for debit */
}

.record-amount.credit {
  color: #27ae60; /* Green for credit */
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: #ffeaea;
  color: #e74c3c;
}

.credit-badge {
  background-color: #e8f7ef;
  color: #27ae60;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a6baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #888;
  background: white;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}



.debit-badge {
  background-color: #e74c3c; /* Red for بد */
}

.credit-badge {
  background-color: #27ae60; /* Green for بس */
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: #e74c3c;
}

.credit {
  color: #27ae60;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.comment-section {
  flex: 1;
  min-width: 0; /* Important for text truncation */
  text-align: right; /* For RTL languages */
}

.amount-section {
  display: flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
}

.detail-value {
  font-size: 12px;
  color: var(--text);
  text-align: right; /* For RTL */
  word-wrap: break-word;
  display: block;
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
}
.sort-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
}

.sort-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.sort-text {
  font-size: 12px;
  color: #495057;
}

.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}
.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* Filter Button */
.filter-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  position: relative;
}

.filter-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.filter-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Filter Panel */
.filter-panel {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.filter-header h4 {
  margin: 0;
  color: #495057;
}

.close-filter-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
}

.filter-content {
  padding: 20px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #495057;
}

.filter-options {
  display: flex;
  gap: 15px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.filter-option input[type="checkbox"] {
  margin: 0;
}

.date-inputs, .amount-inputs {
  display: flex;
  gap: 15px;
}

.date-input, .amount-input {
  flex: 1;
}

.date-input label, .amount-input label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: #6c757d;
}

.date-picker, .amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.apply-filters-btn, .clear-filters-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.apply-filters-btn {
  background: #007bff;
  color: white;
}

.clear-filters-btn {
  background: #6c757d;
  color: white;
}

/* Active Filters */
.active-filters {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.clear-all-filters {
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 12px;
}

.active-filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-tag {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-tag button {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-container{
    width: 100%;
  }
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
  
  .date-inputs, .amount-inputs {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-options {
    flex-direction: column;
    gap: 10px;
  }
  
  .active-filter-tags {
    flex-direction: column;
  }
}
.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 15px;
}

.currency-label {
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap;
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  min-width: 120px;
}

.currency-select:disabled {
  background-color: #f5f5f5;
  opacity: 0.7;
}

.currency-loading {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
}

/* Responsive design for currency selector */
@media (max-width: 768px) {
  .currency-selector {
    margin-right: 10px;
  }
  
  .currency-select {
    min-width: 100px;
    font-size: 0.8rem;
  }
  
  .currency-label {
    font-size: 0.8rem;
  }
}

/* Update the action-bar to accommodate the new currency selector */
.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 15px;
}

/* Ensure proper spacing in mobile view */
@media (max-width: 768px) {
  .action-bar {
    gap: 8px;
    padding: 10px;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
  width: 120px; /* Fixed width for perfect alignment */
  text-align: right;
  flex-shrink: 0;
  margin-left: 10px;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  padding-right: 15px;
}

/* Mobile styles */
@media (max-width: 768px) {
    .info-row {
        flex-direction: row;
        align-items: center;
        padding: 10px 0;
    }
    
    .info-label {
        width: 100px;
        text-align: right;
        margin-left: 5px;
        margin-bottom: 0;
        font-size: 0.85rem;
    }
    
    .info-value {
        width: auto;
        text-align: right;
        padding-right: 5px;
        font-size: 0.85rem;
        font-weight: 500;
        flex: 1;
    }
}


/* Account Summary */

.account-summary {
  background: var(--primary-gradient);
  color: var(--text);
  border-radius: 16px;
  display: inline-flex;
  justify-content: center;
  padding: 10px;
  box-shadow: var(--shadow);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  margin-left: 5px;
  opacity: .9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}

/* Section */

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.1rem;
  color: var(--text);
  margin: 0;
}

.back-arrow {
  background: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  color: var(--text);
}

/* Action Bar */

.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: var(--card-bg);
  border-radius: 10px;
  margin-bottom: 15px;
  border: 1px solid var(--border);
}

/* Buttons */

.print-btn {
  background: var(--primary);
  color: var(--on-primary);
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
}

.print-btn:hover {
  opacity: .9;
}

.sort-btn,
.filter-btn {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text);
}

.sort-btn:hover,
.filter-btn:hover {
  background: var(--hover-bg);
}

.sort-text {
  font-size: 12px;
  color: var(--text-muted);
}

/* Search */

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  background: var(--input-bg);
  color: var(--text);
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
}

/* Currency */

.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency-label {
  font-size: .9rem;
  color: var(--text-muted);
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--input-bg);
  color: var(--text);
}

/* Cards */

.accounting-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  transition: .2s;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}

.record-date {
  font-size: 12px;
  color: var(--text-muted);
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: var(--danger);
}

.credit {
  color: var(--success);
}

/* Badges */

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: .8rem;
  font-weight: bold;
  color: #fff;
}

.debit-badge {
  background: var(--danger);
}

.credit-badge {
  background: var(--success);
}

/* Modal */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay-bg);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 12px;

  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: var(--primary-gradient);
  color: var(--on-primary);
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--on-primary);
  cursor: pointer;
}

.modal-body {
  
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
}

.close-modal-btn {
  background: var(--secondary);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
}

/* Loading */

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: var(--text-muted);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Empty */

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--text-muted);
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px dashed var(--border);
}

/* Filters */

.filter-panel {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border);
}

.filter-label {
  font-weight: 600;
  color: var(--text);
}

.date-picker,
.amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--input-bg);
  color: var(--text);
}

.apply-filters-btn {
  background: var(--primary);
  color: var(--on-primary);
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
}

.clear-filters-btn {
  background: var(--secondary);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
}

/* Animations */

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */

@media (max-width:768px) {

  .search-container{
    width:100%;
  }

  .accounting-grid{
    grid-template-columns:1fr;
  }

}
</style>